import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import HeadIcon from "assets/headIcon.svg";
import BrickIcon from "assets/brickIcon.svg";
import StoreFront from "assets/icons/storefront_light.svg";
import PenIcon from "assets/icons/pen.svg";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const getTimezoneForState = (state) => {
  const timezoneMap = {
    CO: "America/Denver", // MDT
    AR: "America/Chicago", // CDT
    NJ: "America/New_York", // EDT
    PA: "America/New_York", // EDT
  };
  return timezoneMap[state] || dayjs.tz.guess();
};

const TradeInDetails = ({
  customerName,
  address,
  email,
  tradeInId,
  dateCreated,
  dateVerified,
  verifiedQuantity,
  originalEstimate,
  paymentDetails,
  location,
  appointment,
  currentLocation,
  takebackIsVerified,
  onEditSeller,
  onManagePayment,
  showEditSeller = false,
  showManagePayment = false,
  isDropoffStore,
}) => {
  // console.log(verifiedQuantity);
  return (
    <>
      {/* Seller Section */}
      <div className="flex items-start gap-4 mb-8">
        <img src={HeadIcon} alt="Seller" className="w-7 h-7 mt-1" />
        <div className="flex-1 flex flex-col">
          <div className="flex justify-between items-center mb-2">
            <div className="text-2xl">
              <strong>Seller</strong>
            </div>
            {showEditSeller && (
              <div className="flex items-center">
                <img src={PenIcon} alt="Edit" className="w-4 h-4" />
                <span
                  className="inline-block text-[#006CB7] text-sm cursor-pointer whitespace-nowrap ml-2 hover:underline"
                  onClick={onEditSeller}
                >
                  Edit Seller Details
                </span>
              </div>
            )}
          </div>
          <div className="mb-3">
            <span className="font-semibold">Name</span>
            <br />
            {customerName}
          </div>
          <div className="mb-3">
            <span className="font-semibold">Home Address</span>
            <br />
            {address}
          </div>
          <div className="mb-3">
            <span className="font-semibold">Email Address</span>
            <br />
            {email}
          </div>
        </div>
      </div>

      <hr className="my-4 border-gray-300" />

      {/* Trade-In Information Section */}
      <div className="flex items-start gap-4 mb-8">
        <img src={BrickIcon} alt="Trade-In Info" className="w-7 h-7 mt-1" />
        <div className="flex-1">
          <div className="text-2xl mb-2">
            <strong>Trade-In Information</strong>
          </div>
          <div className="mb-3">
            <span className="font-semibold">ID</span>
            <br />
            {tradeInId}
          </div>
          <div className="mb-3">
            <span className="font-semibold">Created</span>
            <br />
            {dateCreated}
          </div>
          {takebackIsVerified && verifiedQuantity ? (
            <>
              {dateVerified && (
                <div className="mb-3">
                  <span className="font-semibold">Verified</span>
                  <br />
                  {dateVerified}
                </div>
              )}
              <div className="mb-3">
                <span className="font-semibold">
                  {takebackIsVerified
                    ? "Verified Quantity"
                    : "Estimated Quantity"}
                </span>
                <br />
                {typeof verifiedQuantity === "number"
                  ? `${verifiedQuantity.toFixed(1)} lbs`
                  : `${parseFloat(verifiedQuantity).toFixed(1)} lbs`}
                {originalEstimate && takebackIsVerified && (
                  <div className="text-tertiary text-sm mt-2">
                    Seller's original estimate:{" "}
                    <span className="font-semibold">
                      {typeof originalEstimate === "number"
                        ? `${originalEstimate.toFixed(1)} lbs`
                        : `${parseFloat(originalEstimate).toFixed(1)} lbs`}
                    </span>
                  </div>
                )}
              </div>
              {paymentDetails && (
                <div className="mb-1 flex items-center justify-between w-full">
                  <div>
                    <span className="font-semibold">
                      {takebackIsVerified
                        ? "Payment details"
                        : "Estimated value"}
                    </span>
                    <br />
                    {paymentDetails}
                  </div>
                  {showManagePayment && takebackIsVerified && (
                    <button
                      className="flex items-center text-tertiary text-base font-medium ml-4 hover:text-blue-700 cursor-pointer"
                      onClick={onManagePayment}
                    >
                      <span className="material-symbols-outlined scale-75 whitespace-nowrap">
                        lock
                      </span>
                      Manage payment
                      <span className="material-symbols-outlined scale-75 whitespace-nowrap">
                        chevron_forward
                      </span>
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mb-3">
                <span className="font-semibold">Estimated Quantity</span>
                <br />
                {parseFloat(originalEstimate).toFixed(1)} lbs
              </div>
              <div className="mb-3">
                <span className="font-semibold">Estimated value</span>
                <br />${Number(originalEstimate * 4).toFixed(2)} LEGO® e-Gift
                Card
              </div>
            </>
          )}
        </div>
      </div>

      <hr className="my-4 border-gray-300" />

      {/* In-Store Appointment/Drop-off Section */}
      <div className="flex items-start gap-4 mb-8">
        <img src={StoreFront} alt="In-Store" className="w-7 h-7 mt-1" />
        <div className="flex-1">
          <div className="text-2xl mb-2">
            <strong>
              {isDropoffStore ? "In-Store Drop-off" : "In-Store Appointment"}
            </strong>
          </div>
          {appointment && !isDropoffStore && (
            <div className="mb-3">
              <span className="font-semibold">Date & time:</span>{" "}
              {(() => {
                const timezone = getTimezoneForState(currentLocation?.state);
                const hasStateTimezone = ["CO", "AR", "NJ", "PA"].includes(
                  currentLocation?.state
                );

                if (hasStateTimezone) {
                  const startDateObj = dayjs(appointment.time).tz(timezone);
                  const endDateObj = dayjs(appointment.end_time).tz(timezone);
                  return `${startDateObj.format("dddd, MMMM D [at] h.mm A")} - ${endDateObj.format("h.mm A")}`;
                } else {
                  const startDateObj = dayjs(appointment.time);
                  const endDateObj = dayjs(appointment.end_time);
                  return `${startDateObj.format("dddd, MMMM D [at] h.mm A")} - ${endDateObj.format("h.mm A")}`;
                }
              })()}
            </div>
          )}
          <div className="mb-3">
            <span className="font-semibold">Location:</span> {location}
          </div>
        </div>
      </div>
    </>
  );
};

TradeInDetails.propTypes = {
  customerName: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  tradeInId: PropTypes.string.isRequired,
  dateCreated: PropTypes.string.isRequired,
  dateVerified: PropTypes.string,
  verifiedQuantity: PropTypes.string.isRequired,
  originalEstimate: PropTypes.string,
  paymentDetails: PropTypes.string,
  location: PropTypes.string.isRequired,
  appointment: PropTypes.object,
  currentLocation: PropTypes.object,
  takebackIsVerified: PropTypes.bool,
  onEditSeller: PropTypes.func,
  onManagePayment: PropTypes.func,
  showEditSeller: PropTypes.bool,
  showManagePayment: PropTypes.bool,
};

export default TradeInDetails;
